import Link from "next/link";
import { cs } from "../../core/classSet";
import { useBooleanState } from "../../core/hooks/useBooleanState";
import { getContactSalesUrl, getLandingPageUrl, getMaintainXUrl } from "../../core/maintainxUrl";
import { NoArgCallback } from "../../typings/utils";
import Button from "../Button/Button";
import ExternalLink from "../ExternalLink/ExternalLink";
import { ArrowIcon, ArrowIcon2, industryIconMap } from "../Icons/Icons";
import Logo from "../Logo/Logo";
import { NAVBAR_SUB_MENUS, NavbarSubMenu, NavbarSubMenuItem } from "../Navbar/Navbar";
import styles from "./NavDrawer.module.css";

function SubNavItem({
  item,
  isResourcesSubMenu,
}: {
  item: NavbarSubMenuItem;
  isResourcesSubMenu: boolean;
}) {
  const Icon = industryIconMap[item.slug];
  item.icon = Icon ? <Icon width={24} height={24} /> : undefined;
  if (item.externalHref) {
    return (
      <li className={styles.navItem}>
        <ExternalLink
          className={cs(styles.subMenuLink, !item.icon && styles.noIcon)}
          href={item.externalHref}
        >
          {!isResourcesSubMenu && <span>{item.icon}</span>}{" "}
          <div className={styles.subMenuLinkText}>{item.label}</div>
        </ExternalLink>
      </li>
    );
  }

  if (item.internalHref) {
    return (
      <li className={styles.navItem}>
        <a
          className={cs(styles.subMenuLink, !item.icon && styles.noIcon)}
          href={getLandingPageUrl(item.internalHref)}
        >
          {!isResourcesSubMenu && <div>{item.icon}</div>}{" "}
          <div className={styles.subMenuLinkText}>{item.label}</div>
        </a>
      </li>
    );
  }

  return (
    <li className={styles.navItem}>
      <Link href={`/${item.slug}`} className={cs(styles.subMenuLink, !item.icon && styles.noIcon)}>
        {!isResourcesSubMenu && <span>{item.icon}</span>}{" "}
        <div className={styles.subMenuLinkText}>{item.label}</div>
      </Link>
    </li>
  );
}

interface INavItemWithSubMenuProps {
  label: string;
  subMenu: NavbarSubMenu;
}

function NavItemWithSubMenu({ label, subMenu }: INavItemWithSubMenuProps) {
  const [isShowingSubNav, , , toggleSubNav] = useBooleanState();
  const isResourcesSubMenu = subMenu.label === "Resources";
  return (
    <li className={styles.navItem}>
      <button className={styles.navLink} type="button" onClick={toggleSubNav}>
        {label}{" "}
        <span className={styles.arrow}>
          <ArrowIcon direction={isShowingSubNav ? "up" : "down"} />
        </span>
      </button>
      {isShowingSubNav ? (
        <ul>
          {subMenu.items.map((item, i) => (
            <SubNavItem key={i} item={item} isResourcesSubMenu={isResourcesSubMenu} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

interface IProps {
  subMenuGpl: NavbarSubMenu;
  show: boolean;
  onHide: NoArgCallback;
}

export default function NavDrawer({ show, onHide }: IProps) {
  return (
    <nav>
      <div
        className={cs(styles.backdrop, show && styles.backdropVisible)}
        onClick={() => onHide()}
      />
      <div className={cs(styles.drawer, show && styles.drawerVisible)}>
        <div className={styles.logo}>
          <a href={getLandingPageUrl()}>
            <Logo width={170} height={24} />
          </a>
        </div>
        <ul>
          <li className={styles.navItem}>
            <a className={styles.navLink} href={getLandingPageUrl()}>
              Home
            </a>
          </li>
          <NavItemWithSubMenu label="Industries" subMenu={NAVBAR_SUB_MENUS.industries} />
          <NavItemWithSubMenu label="Use Cases" subMenu={NAVBAR_SUB_MENUS.useCases} />
          <li className={styles.navItem}>
            <a className={styles.navLink} href={getLandingPageUrl("/pricing")}>
              Pricing
            </a>
          </li>
          <NavItemWithSubMenu label="Resources" subMenu={NAVBAR_SUB_MENUS.resources} />
          <li className={styles.navItem}>
            <a className={styles.navButtonLink} href={getContactSalesUrl()}>
              <Button btnStyle="outline" iconRight={<ArrowIcon2 />}>
                <span className={styles.navButtonOutline}>Book a Tour</span>
              </Button>
            </a>
          </li>
          <li className={styles.navItem}>
            <a className={styles.navButtonLink} href={getMaintainXUrl()}>
              <Button btnStyle="solid" iconRight={<ArrowIcon2 />}>
                Log In to MaintainX
              </Button>
            </a>
          </li>
        </ul>
      </div>
    </nav>
  );
}
